import React, { useState } from 'react';
import {
    ChevronRight,
    ChevronLeft,
    Save,
    User,
    Building2,
    CreditCard,
    FileText,
    CheckCircle2,
    AlertTriangle,
    ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';

const Field = ({ label, section, field, placeholder, type = "text", value, onChange }) => (
    <div className="space-y-2 group">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest group-focus-within:text-yellow-500 transition-colors duration-300 ml-1">{label}</label>
        <div className="relative">
            <Input
                type={type}
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
                className="bg-gray-900/50 border-gray-800 text-white focus:ring-1 focus:ring-yellow-500/50 focus:border-yellow-500/50 h-12 pl-4 rounded-xl transition-all duration-300"
            />
            {value && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 animate-in fade-in zoom-in duration-300">
                    <CheckCircle2 className="h-4 w-4" />
                </div>
            )}
        </div>
    </div>
);

const DataUpdateOnboarding = ({ user, requests, onComplete }) => {
    // requests is array of types e.g., ['personal', 'business']

    // Requests normalization (handle 'financial' sent by backend vs 'banking' expected by UI)
    const normalizedRequests = requests.map(r => r === 'financial' ? 'banking' : r);

    // Sort logic to order steps nicely
    // Sort logic to order steps nicely
    const order = ['personal', 'business', 'banking', 'contract', 'profile'];
    const steps = order.filter(type => normalizedRequests.includes(type)).map(type => {
        switch (type) {
            case 'personal': return { id: 'personal', title: 'Dados Pessoais', subtitle: 'Informações Básicas', icon: User };
            case 'business': return { id: 'business', title: 'Empresa', subtitle: 'Dados Jurídicos', icon: Building2 };
            case 'banking': return { id: 'banking', title: 'Financeiro', subtitle: 'Dados Bancários', icon: CreditCard };
            case 'contract': return { id: 'contract', title: 'Contrato', subtitle: 'Termos e Vigência', icon: FileText };
            case 'profile': return { id: 'profile', title: 'Perfil', subtitle: 'Identificação', icon: User };
            default: return { id: type, title: type, subtitle: 'Outros', icon: AlertTriangle };
        }
    });

    // Safety check if no valid steps are found
    if (steps.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black text-white">
                <div className="text-center space-y-4">
                    <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto" />
                    <h2 className="text-xl font-bold">Nenhuma pendência encontrada</h2>
                    <p className="text-gray-400">Não há dados para atualizar no momento.</p>
                    <Button onClick={() => onComplete({})}>Voltar ao Painel</Button>
                </div>
            </div>
        );
    }

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [formData, setFormData] = useState({
        personalData: { ...user?.staffDetails?.personalData },
        businessData: { ...user?.staffDetails?.businessData },
        bankingData: { ...user?.staffDetails?.bankingData },
        contractData: { ...user?.staffDetails?.contractData }
    });

    const currentStep = steps[currentStepIndex];
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === steps.length - 1;

    // Progress percentage
    const progress = ((currentStepIndex + 1) / steps.length) * 100;

    const handleChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleNext = () => {
        if (isLastStep) {
            onComplete(formData);
        } else {
            setCurrentStepIndex(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setCurrentStepIndex(prev => prev - 1);
    };

    const renderField = (label, section, field, placeholder, type = "text") => (
        <Field
            label={label}
            section={section}
            field={field}
            placeholder={placeholder}
            type={type}
            value={formData[section]?.[field]}
            onChange={(e) => handleChange(section, field, e.target.value)}
        />
    );

    const renderStepContent = () => {
        const stepId = currentStep.id;

        return (
            <motion.div
                key={stepId}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
            >
                {/* Step Header */}
                <div className="flex items-center gap-5 mb-8">
                    <div className="relative">
                        <div className="absolute inset-0 bg-yellow-500/20 blur-xl rounded-full" />
                        <div className="relative h-16 w-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 flex items-center justify-center shadow-2xl">
                            <currentStep.icon className="h-7 w-7 text-yellow-500" />
                        </div>
                    </div>
                    <div>
                        <h2 className="text-3xl font-bold text-white tracking-tight">{currentStep.title}</h2>
                        <p className="text-gray-400 mt-1 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                            {currentStep.subtitle}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {stepId === 'personal' && (
                        <>
                            {renderField("Nome Completo", "personalData", "fullName", "Seu nome completo")}
                            {renderField("CPF", "personalData", "cpf", "000.000.000-00")}
                            {renderField("Data de Nascimento", "personalData", "birthDate", undefined, "date")}
                            {renderField("Telefone", "personalData", "phone", "(00) 00000-0000")}
                        </>
                    )}

                    {stepId === 'business' && (
                        <>
                            {renderField("Razão Social", "businessData", "businessName", "Razão Social da Empresa")}
                            {renderField("Nome Fantasia", "businessData", "fantasyName", "Nome Fantasia")}
                            {renderField("CNPJ", "businessData", "cnpj", "00.000.000/0000-00")}
                            {renderField("CNAE Principal", "businessData", "primaryCnae", "Código CNAE")}
                            {renderField("Data de Abertura", "businessData", "openingDate", undefined, "date")}
                            <div className="col-span-1 md:col-span-2">
                                {renderField("Endereço Completo", "businessData", "fullAddress", "Rua, Número, Bairro, Cidade - UF")}
                            </div>
                        </>
                    )}

                    {stepId === 'banking' && (
                        <>
                            {renderField("Banco", "bankingData", "bank", "Nome do Banco")}
                            {renderField("Tipo de Conta", "bankingData", "accountType", "Corrente / Poupança")}
                            {renderField("Agência", "bankingData", "branch", "0000")}
                            {renderField("Conta", "bankingData", "account", "00000-0")}

                            <div className="col-span-1 md:col-span-2 bg-gray-900/30 p-6 rounded-2xl border border-gray-800 space-y-4">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest block">Chave PIX</label>
                                <div className="flex flex-col md:flex-row gap-4">
                                    <div className="md:w-1/3">
                                        <select
                                            value={formData.bankingData?.pixKeyType || 'cpf'}
                                            onChange={(e) => handleChange('bankingData', 'pixKeyType', e.target.value)}
                                            className="w-full bg-gray-900 border-gray-700 text-white rounded-xl p-3 focus:ring-yellow-500/50 h-12"
                                        >
                                            <option value="cpf">CPF</option>
                                            <option value="cnpj">CNPJ</option>
                                            <option value="email">Email</option>
                                            <option value="phone">Telefone</option>
                                            <option value="random">Aleatória</option>
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <Input
                                            value={formData.bankingData?.pixKey || ''}
                                            onChange={(e) => handleChange('bankingData', 'pixKey', e.target.value)}
                                            placeholder="Chave PIX"
                                            className="bg-gray-900 border-gray-700 text-white focus:ring-yellow-500/50 h-12 rounded-xl"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-span-1 md:col-span-2">
                                {renderField("Nome do Titular", "bankingData", "holderName", "Nome conforme no banco")}
                            </div>
                        </>
                    )}

                    {stepId === 'contract' && (
                        <div className="col-span-1 md:col-span-2">
                            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-6 flex items-start gap-4">
                                <div className="p-3 bg-yellow-500/20 rounded-xl text-yellow-500">
                                    <AlertTriangle className="h-6 w-6" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-yellow-400 font-bold text-lg">Atenção Necessária</h4>
                                    <p className="text-yellow-200/70 mt-2 leading-relaxed">
                                        Dados contratuais geralmente são definidos pela administração.
                                        Se foi solicitada atualização aqui, por favor verifique se há campos editáveis ou entre em contato com o suporte.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {stepId === 'profile' && (
                        <div className="col-span-1 md:col-span-2 space-y-4">
                            <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-6 flex items-start gap-4 mb-6">
                                <div className="p-3 bg-blue-500/20 rounded-xl text-blue-400">
                                    <User className="h-6 w-6" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-blue-400 font-bold text-lg">Perfil Público</h4>
                                    <p className="text-blue-200/70 mt-2 leading-relaxed">
                                        O Codinome é como você será identificado no sistema.
                                    </p>
                                </div>
                            </div>
                            {renderField("Codinome (Nome de Exibição)", "businessData", "codename", "Seu Codinome")}
                        </div>
                    )}
                </div>
            </motion.div>
        );
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl overflow-y-auto">
            {/* Ambient Background */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-900/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-yellow-900/10 rounded-full blur-[120px]" />
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-4xl relative z-10"
            >
                {/* Header / Progress */}
                <div className="mb-8 flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">Atualização Cadastral</h1>
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <span>Passo {currentStepIndex + 1} de {steps.length}</span>
                            <span className="text-gray-600">•</span>
                            <span className="text-yellow-500 font-medium">{currentStep.title}</span>
                        </div>
                    </div>

                    {/* Steps Indicator */}
                    <div className="flex items-center gap-1">
                        {steps.map((_, idx) => (
                            <div
                                key={idx}
                                className={`h-2 rounded-full transition-all duration-300 ${idx === currentStepIndex ? 'w-8 bg-yellow-500' :
                                    idx < currentStepIndex ? 'w-2 bg-yellow-500/50' : 'w-2 bg-gray-800'
                                    }`}
                            />
                        ))}
                    </div>
                </div>

                <Card className="bg-[#09090b]/90 border-gray-800 shadow-2xl relative overflow-hidden backdrop-blur-xl">
                    <CardContent className="p-8 md:p-12 min-h-[500px] flex flex-col justify-between">

                        <AnimatePresence mode="wait">
                            {renderStepContent()}
                        </AnimatePresence>

                        <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-800/50">
                            <Button
                                variant="ghost"
                                onClick={handleBack}
                                disabled={isFirstStep}
                                className={`text-gray-400 hover:text-white hover:bg-gray-800/50 ${isFirstStep ? 'opacity-0 pointer-events-none' : ''}`}
                            >
                                <ChevronLeft className="mr-2 h-4 w-4" />
                                Voltar
                            </Button>

                            <Button
                                onClick={handleNext}
                                className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black font-bold h-12 px-8 shadow-lg shadow-yellow-500/20 rounded-xl transition-all hover:scale-[1.02]"
                            >
                                {isLastStep ? (
                                    <>
                                        Concluir e Salvar
                                        <CheckCircle2 className="ml-2 h-5 w-5" />
                                    </>
                                ) : (
                                    <>
                                        Próximo Passo
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default DataUpdateOnboarding;

